import { useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";
import PageContainer from "../../Components/Page/PageContainer";

interface CompositionToolCard {
  title: string;
  description: string;
  icon: string;
  color: string;
  route: string;
  status: "healthy" | "warning" | "critical" | "info";
  metrics?: {
    total?: number;
    healthy?: number;
    percentage?: number;
  };
}

const CompositionAuditsDashboard = () => {
  const navigate = useNavigate();

  const compositionTools: CompositionToolCard[] = [
    {
      title: "UMPG Sync Tool",
      description:
        "Sync composition ownership data with UMPG systems and audit ownership discrepancies.",
      icon: "pi pi-sync",
      color: "#3b82f6",
      route: "/music_console/music_control_suite/composition_audits/umpg_sync",
      status: "healthy",
      metrics: {
        total: 50000,
        healthy: 47500,
        percentage: 95,
      },
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "healthy":
        return "#22c55e";
      case "warning":
        return "#f59e0b";
      case "critical":
        return "#ef4444";
      case "info":
        return "#3b82f6";
      default:
        return "#6b7280";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "healthy":
        return "Healthy";
      case "warning":
        return "Warning";
      case "critical":
        return "Critical";
      case "info":
        return "Info";
      default:
        return "Unknown";
    }
  };

  return (
    <PageContainer
      HomeMenuBarprops={{
        title: "Composition Audits",
        Tabs: null,
      }}
    >
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Composition Audits Dashboard
          </h1>
          <p className="text-gray-600">
            Audit composition ownership and sync with external systems
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {compositionTools.map((tool, index) => (
            <Card
              key={index}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(tool.route)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <i
                    className={`${tool.icon} text-2xl`}
                    style={{ color: tool.color }}
                  ></i>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {tool.title}
                  </h3>
                </div>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: getStatusColor(tool.status) }}
                  ></div>
                  <span className="text-xs text-gray-500">
                    {getStatusText(tool.status)}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm text-gray-600">{tool.description}</p>

                {tool.metrics && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Health Score</span>
                      <span
                        className="font-medium"
                        style={{ color: getStatusColor(tool.status) }}
                      >
                        {tool.metrics.percentage}%
                      </span>
                    </div>
                    <ProgressBar
                      value={tool.metrics.percentage || 0}
                      color={getStatusColor(tool.status)}
                      showValue={false}
                      className="h-2"
                    />
                    {tool.metrics.total && (
                      <div className="text-xs text-gray-500">
                        {tool.metrics.healthy} of {tool.metrics.total} items
                      </div>
                    )}
                  </div>
                )}

                <Button
                  label="Access Tool"
                  icon="pi pi-arrow-right"
                  className="w-full mt-4"
                  severity="secondary"
                  outlined
                />
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-8 p-6 bg-blue-50 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            About Composition Audits
          </h3>
          <p className="text-blue-800">
            Composition audits ensure that all musical compositions have proper
            ownership records and are correctly synchronized with external
            rights management systems like UMPG. This helps maintain accurate
            royalty distribution and rights tracking.
          </p>
        </div>
      </div>
    </PageContainer>
  );
};

export default CompositionAuditsDashboard;
